import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { formatMoney } from '../../../shared/format/entity-detail.js';
import { formatPersonName } from '../../../shared/format/index.js';
import { useDeleteDealMutation } from '../hooks/useDealMutations.js';
import { DealStageStepper } from './DealStageStepper.js';
import { getSmartTagsForDeal } from './SmartTagBadge.js';
import type { DealDetail, DealSalesHealthSummary } from '../api/deals.client.js';

export function DealDetailHeader({
  deal,
  health,
  onChat,
  onEdit,
  onNote,
  onHistory,
}: {
  deal: DealDetail;
  health: DealSalesHealthSummary | undefined;
  onChat: () => void;
  onEdit: () => void;
  onNote: () => void;
  onHistory: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const remove = useDeleteDealMutation();
  const state = location.state as { dealListUrl?: string } | null;
  const backTo = state?.dealListUrl?.startsWith('/deals?') ? state.dealListUrl : '/deals';
  const company = deal.company ?? deal.contact?.companyRef;
  const contactName = deal.contact
    ? formatPersonName(deal.contact.firstName, deal.contact.lastName)
    : '';
  const score =
    health && typeof health.healthScore === 'number' && Number.isFinite(health.healthScore)
      ? `${Math.round(health.healthScore)}%`
      : '—';
  const stalled = getSmartTagsForDeal(deal).includes('stalled');
  return (
    <EntityHeader
      backTo={backTo}
      backLabel={t('crm.deal_card.back_to_deals')}
      title={deal.title}
      subtitle={
        <>
          <span style={MONO_NUMERIC_STYLE}>{formatMoney(deal.amount, deal.currency)}</span>
          {deal.contact ? (
            <>
              {' '}
              ·{' '}
              <Link to={`/contacts/${deal.contact.id}`}>
                {formatPersonName(deal.contact.firstName, deal.contact.lastName)}
              </Link>
            </>
          ) : null}
          {company && company.name !== contactName ? (
            <>
              {' '}
              · <Link to={`/companies/${company.id}`}>{company.name}</Link>
            </>
          ) : null}
        </>
      }
      chips={[
        ...(health || stalled
          ? [
              <Tooltip
                key="health"
                title={health?.reasons
                  ?.map((reason) => t(`crm.deal_detail.reason_${reason}`))
                  .join(' · ')}
              >
                <span data-testid="deal-health-score-badge">
                  <EntityStatusChip
                    tone={
                      health?.healthLevel === 'critical' || (!health && stalled)
                        ? 'error'
                        : health?.healthLevel === 'attention'
                          ? 'warning'
                          : 'success'
                    }
                  >
                    {health
                      ? `${score} · ${t(`crm.deal_detail.risk_${health.healthLevel}`)}`
                      : t('crm.smart_tags.stalled')}
                  </EntityStatusChip>
                </span>
              </Tooltip>,
            ]
          : []),
      ]}
      primary={{
        key: 'chat',
        testId: 'deal-open-chat',
        label: t('crm.entity_detail.open_chat'),
        icon: <MessageOutlined aria-hidden />,
        onClick: onChat,
      }}
      secondary={[{ key: 'edit', label: t('crm.entity_detail.edit'), onClick: onEdit }]}
      overflow={[
        { key: 'note', label: t('crm.deal_card.quick_add_note'), onClick: onNote },
        { key: 'history', label: t('crm.entity_detail.history'), onClick: onHistory },
        {
          key: 'delete',
          label: t('crm.deal_detail.delete'),
          danger: true,
          disabled: remove.isPending,
          confirm: {
            title: t('crm.deal_detail.delete'),
            description: t(
              'crm.features.features.deals.pages.dealcardpage.задачи_встречи_и_документы_останутся_но_по',
            ),
          },
          onClick: () => remove.mutateAsync(deal.id).then(() => navigate(backTo)),
        },
      ]}
    >
      <DealStageStepper dealId={deal.id} stage={deal.stage} />
    </EntityHeader>
  );
}
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
